import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChartColumn, History, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DRBFMPageHeaderProps {
  hideActions?: boolean;
  resultsOpen?: boolean;
  onNewAnalysis?: () => void;
  mainlineEnabled?: boolean;
  mainlineOpen?: boolean;
  onOpenMainline?: () => void;
  backLabel: string;
  onBack: () => void;
  isCaseMode: boolean;
  advancedModesEnabled: boolean;
  workflowMode: 'quick' | 'staged';
  onWorkflowModeChange: (mode: 'quick' | 'staged') => void;
  onToggleCaseMode: () => void;
  onOpenHistory: () => void;
}

const DRBFMPageHeader: React.FC<DRBFMPageHeaderProps> = ({
  hideActions = false,
  resultsOpen = false,
  onNewAnalysis,
  mainlineEnabled,
  mainlineOpen,
  onOpenMainline,
  backLabel,
  onBack,
  isCaseMode,
  advancedModesEnabled,
  workflowMode,
  onWorkflowModeChange,
  onToggleCaseMode,
  onOpenHistory,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-4">
        <Button variant="ghost" size="icon-lg" aria-label={backLabel} onClick={onBack}>
          <ArrowLeft strokeWidth={1.9} />
        </Button>
        <ChartColumn className="size-8 shrink-0 text-primary" strokeWidth={1.6} aria-hidden="true" />
        <h1 className="revy-cjk min-w-0 text-xl sm:text-2xl font-semibold leading-[1.4] tracking-tight">
          {t('drbfm.title')}{resultsOpen && <> · {t('drbfm.resultsWorkspace.title')}</>}
        </h1>
      </div>

      {/* Filled vs outlined is the selected state, not emphasis. */}
      {!hideActions && (
        <div className="flex flex-wrap justify-end gap-2">
          {!resultsOpen && (mainlineEnabled || mainlineOpen) && <Button variant={mainlineOpen ? 'default' : 'outline'}
            aria-pressed={mainlineOpen} onClick={onOpenMainline}>
            {t(mainlineOpen ? 'drbfm.mainline.normalView' : 'drbfm.mainline.open')}
          </Button>}
          {!resultsOpen && advancedModesEnabled && !isCaseMode && !mainlineOpen && (
            <>
              <Button
                variant={workflowMode === 'staged' ? 'default' : 'outline'}
                aria-pressed={workflowMode === 'staged'}
                onClick={() => onWorkflowModeChange('staged')}
              >
                <span className="revy-cjk">{t('drbfm.workflow.stagedMode', '工程別に進める')}</span>
              </Button>
              <Button
                variant={workflowMode === 'quick' ? 'default' : 'outline'}
                aria-pressed={workflowMode === 'quick'}
                onClick={() => onWorkflowModeChange('quick')}
              >
                <span className="revy-cjk">{t('drbfm.workflow.quickMode', 'クイック実行')}</span>
              </Button>
            </>
          )}
          {!resultsOpen && advancedModesEnabled && (
            <Button
              variant={isCaseMode ? 'default' : 'outline'}
              aria-pressed={isCaseMode}
              onClick={onToggleCaseMode}
            >
              <span className="revy-cjk">
                {isCaseMode ? t('drbfm.case.normalMode') : t('drbfm.case.startCase')}
              </span>
            </Button>
          )}
          <Button variant="outline" onClick={onOpenHistory}>
            <History strokeWidth={1.9} />
            <span className="revy-cjk">{t('drbfm.history.title', '分析履歴')}</span>
          </Button>
          {resultsOpen && <Button onClick={onNewAnalysis}>
            <Plus aria-hidden="true" />{t('drbfm.results.newAnalysis')}
          </Button>}
        </div>
      )}
    </div>
  );
};

export default DRBFMPageHeader;
